import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { AttributeNode } from '@/types/schemas';
import type { JsonType } from '@/domain/entities/property-schema';
import { cloneDeep, JSON_TYPE_OPTIONS, makeEmptyAttribute, normalizeNodeForType } from '../utils/schema-utils';
import { CustomKeysEditor } from './CustomKeysEditor';
import { EnumEditor } from './EnumEditor';

export function AttributeEditor(props: {
  node: AttributeNode;
  onChange: (next: AttributeNode) => void;
  onRemove: () => void;
  depth?: number;
  collapsible?: boolean;
  isArrayItem?: boolean;
  defaultOpen?: boolean;
  disabled?: boolean;
}) {
  const {
    node,
    onChange,
    onRemove,
    depth = 0,
    collapsible = true,
    defaultOpen = true,
    disabled = false,
    isArrayItem = false,
  } = props;

  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => setOpen(defaultOpen), [defaultOpen]);

  const update = (patch: Partial<AttributeNode>) => {
    if (disabled) return;
    onChange({ ...node, ...patch });
  };

  const showEnum = node.type === 'string';
  const showObjectChildren = node.type === 'object';
  const showArrayItem = node.type === 'array';

  return (
    <div className="space-y-3 rounded-2xl border p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {collapsible ? (
            <button
              type="button"
              className="hover:bg-muted rounded-md p-1"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle"
              disabled={disabled}
            >
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="inline-block h-6 w-6" />
          )}

          <div className="text-muted-foreground text-sm">
            {depth === 0 ? 'Block' : 'Attribute'}
            {node.key.trim() ? (
              <>
                : <span className="text-foreground font-mono">{node.key.trim()}</span>
              </>
            ) : null}
          </div>
        </div>

        <Button type="button" variant="outline" onClick={onRemove} disabled={disabled}>
          <Trash2 className="mr-2 h-4 w-4" />
          Remove
        </Button>
      </div>

      {open ? (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-12 md:items-end">
            <div className="space-y-2 md:col-span-4">
              <Label>{depth === 0 ? 'Block key' : 'Attribute key'}</Label>
              {!isArrayItem ? (
                <>
                  <Input value={node.key} onChange={(e) => update({ key: e.target.value })} disabled={disabled} />
                  <p className="text-muted-foreground text-xs">
                    Stored as <span className="font-mono">attributes["{node.key || '...'}"]</span>
                  </p>
                </>
              ) : (
                <div className="text-muted-foreground text-sm">Item schema (no key)</div>
              )}
            </div>

            <div className="space-y-2 md:col-span-3">
              <Label>Type</Label>
              <Select
                value={node.type}
                onValueChange={(v) => !disabled && onChange(normalizeNodeForType(node, v as JsonType))}
                disabled={disabled}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {JSON_TYPE_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-3">
              <Label>Required</Label>
              <div className="mt-2">
                <Switch checked={node.required} onCheckedChange={(v) => update({ required: v })} disabled={disabled} />
              </div>
              <p className="text-muted-foreground text-xs">Controls the parent’s required list.</p>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Nullable</Label>
              <div className="mt-2">
                <Switch
                  checked={!!node.nullable}
                  onCheckedChange={(v) => update({ nullable: v })}
                  disabled={disabled}
                />
              </div>
              <p className="text-muted-foreground text-xs">
                Allows <span className="font-mono">null</span> as a value.
              </p>
            </div>
          </div>

          <CustomKeysEditor node={node} updateNode={(next) => update(next)} disabled={disabled} />

          {showEnum ? (
            <EnumEditor
              values={node.enum ?? []}
              onChange={(next) => update({ enum: next.length ? next : undefined })}
              disabled={disabled}
            />
          ) : null}

          {showObjectChildren ? (
            <div className="space-y-3 rounded-xl border p-3">
              <div className="flex items-center justify-between">
                <Label>{depth === 0 ? 'Block attributes' : 'Nested attributes'}</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => update({ attributes: [...(node.attributes ?? []), makeEmptyAttribute()] })}
                  disabled={disabled}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add attribute
                </Button>
              </div>

              {(node.attributes ?? []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No attributes yet.</p>
              ) : (
                <div className="space-y-3">
                  {(node.attributes ?? []).map((child, index) => (
                    <div key={child.id} className="border-l pl-3">
                      <AttributeEditor
                        node={child}
                        depth={depth + 1}
                        disabled={disabled}
                        onChange={(nextChild) => {
                          const next = cloneDeep(node);
                          next.attributes = [...(next.attributes ?? [])];
                          next.attributes[index] = nextChild;
                          onChange(next);
                        }}
                        onRemove={() => {
                          const next = cloneDeep(node);
                          next.attributes = (next.attributes ?? []).filter((_, i) => i !== index);
                          onChange(next);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {showArrayItem ? (
            <div className="space-y-3 rounded-xl border p-3">
              <Label>Array item schema</Label>
              <div className="border-l pl-3">
                {node.items ? (
                  <AttributeEditor
                    node={node.items}
                    depth={depth + 1}
                    disabled={disabled}
                    isArrayItem
                    onChange={(nextItem) => update({ items: nextItem })}
                    onRemove={() => update({ items: undefined })}
                    defaultOpen={true}
                  />
                ) : (
                  <div className="space-y-2">
                    <p className="text-muted-foreground text-sm">No item schema. Add one:</p>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        update({
                          items: {
                            id: cryptoIdFallback('item'),
                            key: 'item',
                            type: 'string',
                            required: false,
                          } as AttributeNode,
                        })
                      }
                      disabled={disabled}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add item schema
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// local safe id helper so this component stays standalone
function cryptoIdFallback(prefix = 'id'): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}
